/**
 * Conversation history model for the Evie widget.
 *
 * Everything here is plain JSON so it can be persisted in localStorage today and sent to a backend
 * later without transformation. Widget-specific shapes (React components, Date objects) are derived
 * from these records, never stored.
 */

export type ChatRole = 'user' | 'bot';

export type MessageRating = 'liked' | 'disliked';

export interface QuickButton {
  label: string;
  /**
   * `preset:<presetId>` or `case:<caseRefNo>`. Any other value is sent as if the user typed it
   * (e.g. a "Use jane@example.com" button whose value is the email).
   */
  value: string;
}

export interface ConversationMessage {
  id: string;
  role: ChatRole;
  /** Markdown text as shown in the widget. */
  text: string;
  /** Epoch milliseconds. */
  createdAt: number;
  /** Preset that produced this bot message, or that a user message was routed to. */
  intentId?: string;
  /** Quick buttons offered after this bot message. Re-shown on rehydrate when it is the last message. */
  quickButtons?: QuickButton[];
  /** Thumbs feedback on a bot message. */
  feedback?: MessageRating;
  /** Case this message is about, if any. */
  caseRefNo?: string;
}

/**
 * Where the "Have support contact me" flow is up to:
 * - `summary`: waiting for what they need help with;
 * - `contact`: no account email, so waiting for an email or phone number before filing;
 * - `handed_off`: filed with the account email; "Use a different email" / "Call me instead" are on offer;
 * - `email` / `phone`: waiting for the replacement contact after one of those taps.
 */
export type ContactStep = 'summary' | 'contact' | 'handed_off' | 'email' | 'phone';

export interface ContactDraft {
  /** What the dentist needs help with; becomes the ticket's problem statement. */
  summary?: string;
  /** Why the ticket is being raised; becomes the triage `reason`. */
  reason?: 'talk_to_human' | 'report_problem' | 'unresolved' | 'session_feedback' | 'case_question' | 'emergency';
  /** Failed attempts to give a usable email or phone number. */
  attempts?: number;
  /** Carried onto the ticket; 'high' when the dentist was frustrated. */
  priority?: EscalationPriority;
  /** The contact the ticket was filed with, so a later change can say what it replaced. */
  handoffContact?: { email?: string; phone?: string };
}

export type EscalationPriority = 'normal' | 'high';

/** A non-status ask about a case, waiting for the dentist to confirm it goes to the case's Messages tab. */
export interface CaseQuestionDraft {
  caseRefNo: string;
  caseId?: string;
  /** What gets posted, word for word. */
  text: string;
}

export interface SessionContext {
  /** Preset waiting for a follow-up answer (e.g. report_problem waiting for the description). */
  pendingIntentId?: string;
  selectedCaseRefNo?: string;
  selectedCaseId?: string;
  /** The last question no preset could answer; seeds a support ticket if the user escalates. */
  lastUnansweredText?: string;
  /** Where the "Have support contact me" flow is up to. */
  contactStep?: ContactStep;
  contactDraft?: ContactDraft;
  /** The case question Evie offered to send to the design team. */
  caseQuestionDraft?: CaseQuestionDraft;
  /** How many times this session the user has asked for support. */
  supportAsks?: number;
  /** Evie has already shown a case, a delay explanation, a design-team hand-off, an LLM answer, or admitted defeat. */
  helpAttempted?: boolean;
  /** Consecutive user messages that read as frustrated. */
  frustrationStreak?: number;
  /** Support call-back may be offered without deflecting (second ask, second irate turn, explicit demand, or Evie failed). */
  escalationUnlocked?: boolean;
  /** Set once the user was frustrated; carried onto any ticket raised later in the session. */
  escalationPriority?: EscalationPriority;
  /** The voice drawn when the session started (presets/personalities.ts). Absent on older sessions, which read as 'classic'. */
  personalityId?: string;
}

export interface SessionFeedback {
  resolved: boolean;
  score: number;
  comment: string;
  submittedAt: number;
}

export interface TriageRecord {
  reason: string;
  createdAt: number;
  accepted: boolean;
  mode: 'remote' | 'noop';
  ticketRef?: string;
}

export type TicketState = 'in_progress' | 'open' | 'closed';

/** The Jira ticket mirroring this session; see ticket/types.ts for the full shape. */
export interface ConversationTicket {
  ref: string;
  url?: string;
  token?: string;
  state: TicketState;
  syncedCount: number;
  escalation?: {
    reason: 'talk_to_human' | 'report_problem' | 'unresolved' | 'session_feedback' | 'case_question' | 'emergency';
    userMessage?: string;
    contact?: { email?: string; phone?: string };
    priority?: EscalationPriority;
  };
}

export interface ConversationSession {
  schemaVersion: 1;
  id: string;
  /** Lower-cased user email; namespaces storage so two users on one browser never share history. */
  userKey: string;
  practiceId?: string;
  startedAt: number;
  lastActivityAt: number;
  endedAt?: number;
  messages: ConversationMessage[];
  context: SessionContext;
  sessionFeedback?: SessionFeedback;
  triageRequests: TriageRecord[];
  /** Set once the ticket API accepted the first message; absent where the API is not configured. */
  ticket?: ConversationTicket;
  /** When creating the ticket last failed, so the engine retries after a pause rather than every turn. */
  ticketFailedAt?: number;
}

export const CONVERSATION_SCHEMA_VERSION = 1 as const;
